import {useMemo} from 'react';
import {
  Column,
  IdType,
  Row,
  TableState,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import {useAddSelectionColumn} from '../tableSelection/useAddSelectionColumn';

/*
 * Props.
 */

export interface UseAnalyticsTableProps<T extends object> {
  columns: Array<Column<T>>;
  data: Array<T>;
  disableSelection?: boolean;
  initialState?: Partial<TableState<T>>;
  disableSortBy?: boolean;
  disableGlobalFilter?: boolean;
  hiddenColumnIds?: Array<string>;
  globalFilterOption?: GlobalFilterFunction<T>;
  autoResetPage?: boolean;
}

type GlobalFilterFunction<T extends object> = (
  rows: Array<Row<T>>,
  columnIds: Array<IdType<T>>,
  filterValue: any,
) => Array<Row<T>>;

/*
 * Hook.
 */

export const useAnalyticsTable = <T extends object>(options: UseAnalyticsTableProps<T>) => {
  const {
    columns,
    data,
    initialState,
    disableSelection,
    globalFilterOption,
    disableGlobalFilter,
    hiddenColumnIds,
    disableSortBy,
    autoResetPage,
  } = options;

  const plugins = useMemo(
    () => [
      useGlobalFilter,
      useSortBy,
      usePagination,
      useRowSelect,
      ...(disableSelection ? [] : [useAddSelectionColumn]),
    ],
    [disableSelection],
  );

  const tableInstance = useTable<T>(
    {
      columns,
      data,
      initialState: {...initialState, hiddenColumns: hiddenColumnIds},
      globalFilter: globalFilterOption,
      disableSortRemove: true,
      autoResetSortBy: false,
      disableSortBy,
      disableGlobalFilter,
      autoResetPage,
    },
    ...plugins,
  );

  return {
    tableInstance,
  };
};
